import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {DataGridMiscContext, DataGridStateContext} from "../../../../index";
import classnames from "classnames";
import {DataGridColumnAlignments, DataGridColumnHighlightingMechanismModes, DataGridColumnHighlightingMechanisms, DataGridColumnTypes, DataGridExoticColumnFields} from "../../../../models";
import {buildHighlightSegments, comparisonStyleForText} from "../../../../core/services/utils/highlight";
import {ReactComponent as ChildTogglerIcon} from "../../../../assets/images/body/child-toggler.svg";
import {ReactComponent as GroupTogglerIcon} from "../../../../assets/images/body/group-toggler.svg";
import DataGridCheckbox from "../../../components/checkbox";
import DataGridIconButton from "../../../components/icon-button/inedx";
import DataGridUtils from "../../../../core/services/utils";
import {Menu, MenuItem, Popover, Tooltip} from "@mui/material";
import DataGridCellEditor from "../cell-editor";
import * as ReactIs from "react-is";
import {ReactComponent as CheckIcon} from '../../../../assets/images/boolean/check.svg'
import {ReactComponent as CancelIcon} from '../../../../assets/images/boolean/cancel.svg'
import copy from "clipboard-copy";
import Texts from "../../../../core/models/static/texts";
import {MEASURE_COLUMN_ATTR} from "../virtual-column-heights";

/**
 * Resolves the paint class for a column. Anything other than highlight stays an underline.
 * @param {DataGridColumnHighlightingMechanismModes | undefined} paintMode
 * @return {string}
 */
function highlightPaintModeClass(paintMode) {
    return paintMode === DataGridColumnHighlightingMechanismModes.highlight
        ? 'data-grid-cell-highlight-mode-highlight'
        : 'data-grid-cell-highlight-mode-underline';
}

/**
 * Renders comparison text. Each similarity family has its own color.
 * A thicker underline, or a stronger wash, marks characters that are rarer inside that family.
 * The glyph color stays the cell color so the value remains readable.
 * @param {string} text
 * @param {DataGridCellHighlight | null | undefined} highlight
 * @param {DataGridColumnHighlightingMechanismModes | undefined} paintMode
 * @return {string | JSX.Element[]}
 */
function renderCellHighlightContent(text, highlight, paintMode) {
    if (!highlight?.active) {
        return text;
    }
    const style = comparisonStyleForText(text, highlight);
    if (!style) {
        return text;
    }
    const segments = buildHighlightSegments(text, style.mask, style.degrees, style.cluster);
    if (!segments.some(segment => segment.highlight)) {
        return text;
    }
    const paintClass = highlightPaintModeClass(paintMode);
    return segments.map((segment, index) => (
        segment.highlight
            ? <span
                key={index}
                className={classnames(
                    'data-grid-cell-highlight',
                    paintClass,
                    `data-grid-cell-highlight-cluster-${segment.cluster}`,
                    `data-grid-cell-highlight-degree-${segment.degree}`,
                )}
            >{segment.text}</span>
            : <span key={index}>{segment.text}</span>
    ));
}

/**
 * Returns the highlighted node for a custom cell renderer.
 * Columns with no mechanism receive the plain string.
 * @param {unknown} value
 * @param {DataGridColumnHighlightingMechanisms | undefined} mechanism
 * @param {DataGridCellHighlight | null | undefined} highlight
 * @param {DataGridColumnHighlightingMechanismModes | undefined} paintMode
 * @return {string | JSX.Element}
 */
function renderGetHighlightText(value, mechanism, highlight, paintMode) {
    const text = value == null ? '' : String(value);
    if (mechanism !== DataGridColumnHighlightingMechanisms.diff && mechanism !== DataGridColumnHighlightingMechanisms.eq) {
        return text;
    }
    return (
        <span className={'data-grid-cell-highlight-text'}>
            {renderCellHighlightContent(text, highlight, paintMode)}
        </span>
    );
}


/**
 * @param {DataGridInternalCell} cell
 * @param {DataGridDensities} density
 * @param {string} rowKey
 * @param {Function} setDetailedPanelOpen
 * @param {Function} toggleSelection
 * @param {boolean} detailedPanelOpen
 * @param {boolean} hasDetailedPanel
 * @param {boolean} rowSelected
 * @param {boolean} detailedPanelVisible
 * @param {boolean} groupedRowsOpen
 * @param {boolean} groupedRowsVisible
 * @param {Function} setGroupedRowsOpen
 * @param {Function} startScrolling
 * @param {React.CSSProperties} tdStyle
 * @param {number | undefined} rowLockHeight Minimum td height taken from the row lock. The inner content keeps {@code tdStyle.minHeight}.
 * @return {JSX.Element}
 */
const DataGridBodyCell = ({
                              cell,
                              rowKey,
                              density,
                              setDetailedPanelOpen,
                              toggleSelection,
                              detailedPanelOpen,
                              rowSelected,
                              hasDetailedPanel,
                              detailedPanelVisible,
                              groupedRowsOpen,
                              groupedRowsVisible,
                              setGroupedRowsOpen,
                              startScrolling: startScrollingProp,
                              tdStyle,
                              rowLockHeight,
                          }) => {
    const {group} = useContext(DataGridStateContext)
    const {classNames, singleSelectionOnly, showSelectionOnHover} = useContext(DataGridMiscContext);
    const [popover, setPopover] = useState(null);
    const [contextMenu, setContextMenu] = useState(null);
    const id = useRef(DataGridUtils.createUUId(true));
    const cellRef = useRef();
    const innerCellRef = useRef();
    const copyFeedbackTimer = useRef();

    /**@type {DataGridInternalColumn}*/
    const column = useMemo(() => cell?.column, [cell?.column])

    const showRowToggler = useMemo(() => {
        if (!column?.name)
            return false;
        const groups = Array.isArray(group) ? group : !group ? undefined : [group];
        if (groups === undefined)
            return false;
        return groups.find(e => e.showOnColumn === column?.name) !== undefined;
    }, [group?.showOnColumn, column?.name])

    /**
     * Memo version of the boolean indicating if secondary font family is needed
     * @type {boolean}
     */
    const needsDifferentFont = useMemo(() => [DataGridColumnTypes.money, DataGridColumnTypes.number].includes(column?.type), [column?.type])

    const width = useMemo(() =>
            `${Math.max(column?.width?.size ?? 0, column?.width?.minWidth ?? 0)}px`,
        [column.width])

    const pinOffset = useMemo(() =>
            !column?.pinned
                ? undefined
                : `${column?.leftPinOffset ?? column?.rightPinOffset}px`,
        [column?.pinned, column?.leftPinOffset, column?.rightPinOffset])

    /**@type {'right' | 'left' | 'center'}*/
    const popoverHorizontalPosition = useMemo(() => {
        switch (column?.alignment) {
            case DataGridColumnAlignments.center:
                return 'center';
            case DataGridColumnAlignments.left:
            default:
                return 'right';
            case DataGridColumnAlignments.right:
                return 'left';
        }
    }, [column?.alignment])

    const editable = useMemo(() =>
            cell?.content?.editable &&
            React.isValidElement(cell?.content?.EditableContentComponent),
        [cell?.content?.editable, cell?.content?.EditableContentComponent])

    /**
     * Initiates the scrolling of the data-grid content if the column is not pinned.
     * @param {MouseEvent | TouchEvent} e
     */
    const startScrolling = (e) => {
        if (!column || column.pinned) {
            return;
        }
        startScrollingProp(e);
    }

    /**
     * Closes the popover
     */
    const closePopover = () => setPopover(null);

    /**
     * Opens the popover
     * @param {Event} e
     */
    const openPopover = (e) => {
        if (!editable)
            return;
        setPopover(e.currentTarget)
    };

    /**
     * Closes the cell copy context menu.
     */
    const closeContextMenu = () => setContextMenu(null);

    /**
     * Opens the cell copy context menu when the cell has a copyable value.
     * @param {MouseEvent} e
     */
    const openContextMenu = (e) => {
        const copyText = DataGridUtils.getCellCopyText({
            cell,
            innerCellEl: innerCellRef.current,
        });

        if (!copyText) {
            return;
        }

        e.preventDefault();
        e.stopPropagation();
        closePopover();
        setContextMenu({
            mouseX: e.clientX + 2,
            mouseY: e.clientY - 6,
            copyText,
            copied: false,
        });
    };

    /**
     * Copies the currently selected cell context menu value.
     */
    const copyContextMenuValue = () => {
        if (!contextMenu?.copyText) {
            return;
        }

        void copy(contextMenu.copyText).then(() => {
            setContextMenu(prevState => prevState ? {...prevState, copied: true} : prevState);
            if (copyFeedbackTimer.current) {
                clearTimeout(copyFeedbackTimer.current);
            }
            copyFeedbackTimer.current = setTimeout(closeContextMenu, 750);
        });
    };

    useEffect(() => () => {
        if (copyFeedbackTimer.current) {
            clearTimeout(copyFeedbackTimer.current);
        }
    }, []);

    const groupedRowToggler = useMemo(() => (
        cell?.showGroupToggler && cell?.groupedRowsLength > 0
            ? <Tooltip
                arrow
                title={`${groupedRowsVisible ? "Collapse" : "Expand"} ${cell?.groupedRowsLength} ${cell?.groupedRowsLength !== 1 ? 'rows' : 'row'}`}
            >
                <DataGridIconButton
                    className={classnames(
                        'data-grid-group-toggler-button',
                        {'open': groupedRowsVisible},
                    )}
                    onClick={() => setGroupedRowsOpen(prevState => !prevState)}
                >
                    <GroupTogglerIcon/>
                </DataGridIconButton>
            </Tooltip>
            : <DataGridIconButton
                className={classnames(
                    'data-grid-group-toggler-button invisible',
                    {'open': groupedRowsVisible},
                    {'half-margin': cell?.groupedRowsLength <= 0},
                )}
            >
                <GroupTogglerIcon/>
            </DataGridIconButton>
    ), [groupedRowsVisible, cell?.showGroupToggler, cell?.groupedRowsLength])

    /**
     * Generates the element of the row cell content based on the column.
     *
     * @param {DataGridCellContent} content
     * @return {JSX.Element}
     */
    const cellContent = useMemo(() => {
        const content = cell?.content?.value;
        let cellContent;

        if (typeof content === 'undefined' || content === null)
            cellContent = '--';
        else if (typeof content === 'function') {
            cellContent = content({
                format: column?.format,
                detailedPanelOpen: detailedPanelOpen,
                toggleDetailedPanelOpen: () => setDetailedPanelOpen(prevState => !prevState),
                groupedRowsOpen: groupedRowsOpen,
                toggleGroupedRowsOpen: () => setGroupedRowsOpen(prevState => !prevState),
                rowSelected: rowSelected,
                toggleRowSelection: toggleSelection,
                openEditPopover: () => openPopover({currentTarget: cellRef.current}),
                getHighlightText: (text) => renderGetHighlightText(text, column?.highlightingMechanism, cell?.highlight, column?.highlightingMechanismMode),
            });
        } else {
            if (typeof column?.format === 'function')
                cellContent = column?.format(content);
            else if (React.isValidElement(content))
                cellContent = content;
            else if (typeof content === 'number' && column?.type === DataGridColumnTypes.number) {
                cellContent = content.toFixed(2);
            } else {
                cellContent = content;
            }
        }
        return cellContent;
    }, [
        cell?.content?.value,
        cell?.highlight,
        column?.format,
        column?.type,
        column?.highlightingMechanism,
        column?.highlightingMechanismMode,
        detailedPanelOpen,
        rowSelected,
        groupedRowsOpen,
    ])

    const defaultCellContent = useMemo(() => {
        const childrenProps = {
            onMouseDown: e => e.stopPropagation(),
            onMouseMove: e => e.stopPropagation(),
            onTouchStart: e => e.stopPropagation(),
            onTouchMove: e => e.stopPropagation(),
        }

        let children = cellContent;
        if (React.isValidElement(children)) {
            if (React.Children.count(children) === 1) {
                const firstChild = React.Children.only(children);
                if (ReactIs.typeOf(firstChild) === React.Fragment) {
                    children = firstChild.props.children;
                }

            }

            return (
                <>
                    {showRowToggler && groupedRowToggler}
                    {React.Children.map(
                        children,
                        (child) => child && React.cloneElement(child, childrenProps)
                    )}
                </>
            );
        }
        const comparable = column?.highlightingMechanism === DataGridColumnHighlightingMechanisms.diff
            || column?.highlightingMechanism === DataGridColumnHighlightingMechanisms.eq;
        const textClassName = classnames('data-grid-row-body-inner-cell-text', {
            'data-grid-cell-highlight-text': comparable,
        });

        if (column?.type === DataGridColumnTypes.boolean) {
            children = (
                <>
                    {
                        !!cellContent &&
                        <CheckIcon className={'boolean-check-icon'}/>
                    }
                    {
                        !cellContent &&
                        <CancelIcon className={'boolean-cancel-icon'}/>
                    }
                </>
            )
        }

        const renderedText = children == null || cell?.content?.value == null
            ? '--'
            : comparable && (typeof children === 'string' || typeof children === 'number' || typeof children === 'boolean')
                ? renderCellHighlightContent(String(children), cell?.highlight, column?.highlightingMechanismMode)
                : children;

        return (
            <>
                {showRowToggler && groupedRowToggler}
                <p {...childrenProps} className={textClassName}>
                    {renderedText}
                </p>
            </>
        );
    }, [
        cell?.content?.value,
        cellContent,
        cell?.highlight,
        column?.highlightingMechanism,
        column?.highlightingMechanismMode,
        column?.type,
        groupedRowToggler,
        showRowToggler
    ])

    const content = useMemo(() => {
        switch (column?.name) {
            case DataGridExoticColumnFields.selection:
                return (!!cellContent && !(typeof cell?.content?.value === 'undefined' || cell?.content?.value === null))
                    ? defaultCellContent
                    : <>
                        <DataGridCheckbox
                            checked={rowSelected}
                            onClick={toggleSelection}
                            radio={singleSelectionOnly}
                            className={classnames({
                                'show-on-hover': singleSelectionOnly && showSelectionOnHover,
                                'force-show': rowSelected,
                            })}
                        />
                    </>
            case DataGridExoticColumnFields.detailedPanelToggler:
                if (!hasDetailedPanel)
                    return <></>;
                return <>
                    <DataGridIconButton
                        className={classnames(
                            'data-grid-child-toggler-button',
                            {'open': detailedPanelVisible},
                        )}
                        onClick={() => setDetailedPanelOpen(prevState => !prevState)}
                        onDoubleClick={DataGridUtils.modifyEvent}
                    >
                        <ChildTogglerIcon/>
                    </DataGridIconButton>
                </>
            case DataGridExoticColumnFields.detailedPanel:
            case DataGridExoticColumnFields.spacer:
            case undefined:
            case null:
                // child column should never be rendered in the first place. this is just a
                // placeholder in case of any bugs.
                return <></>
            default:
                return defaultCellContent
        }
    }, [
        hasDetailedPanel,
        defaultCellContent,
        column?.name,
        rowSelected,
        detailedPanelVisible,
        singleSelectionOnly,
        cellContent,
        cell?.content?.value,
    ]);

    return (
        <>
            <td
                onMouseDown={startScrolling}
                onTouchStart={startScrolling}
                onDoubleClick={openPopover}
                onContextMenu={openContextMenu}
                ref={cellRef}
                {...(column?.name != null ? {[MEASURE_COLUMN_ATTR]: column.name} : {})}
                className={classnames(
                    'data-grid-row-body-cell',
                    column?.alignment,
                    classNames.cell,
                    {
                        'pinned': column?.pinned,
                        [column?.pinnedType]: column?.pinned,
                        'first-right-pinned': column?.pinned && cell?.firstRightPinned,
                        'last-left-pinned': column?.pinned && cell?.lastLeftPinned,
                        'detailed-panel-visible': detailedPanelVisible,
                        'editable': editable,
                        'secondary': needsDifferentFont,
                    },
                    {
                        [classNames.cellEditable]: editable,
                    }
                )}
                style={{
                    ...tdStyle,
                    minHeight: rowLockHeight ?? tdStyle?.minHeight,
                    '--width': width,
                    '--offset': pinOffset,
                }}>
                <div
                    className={classnames(
                        'data-grid-row-body-inner-cell',
                        classNames.innerCell,
                        {[density]: true},
                        {'data-grid-row-body-inner-cell-custom-element': React.isValidElement(cellContent)}
                    )}
                    ref={innerCellRef}
                    style={{
                        minHeight: tdStyle?.minHeight,
                    }}
                >
                    {content}
                </div>
            </td>
            {
                !!contextMenu &&
                <Menu
                    open={!!contextMenu}
                    onClose={closeContextMenu}
                    anchorReference={'anchorPosition'}
                    anchorPosition={{
                        top: contextMenu.mouseY,
                        left: contextMenu.mouseX,
                    }}
                >
                    <MenuItem onClick={copyContextMenuValue}>
                        {contextMenu.copied ? Texts.copiedToClipboard : Texts.copyCell}
                    </MenuItem>
                </Menu>
            }
            {
                editable &&
                <Popover
                    id={!!popover ? id.current : undefined}
                    elevation={2}
                    open={!!popover}
                    onClose={closePopover}
                    anchorReference={'anchorEl'}
                    className={classnames(
                        "data-grid-popover",
                        "data-grid-cell-editor-popover",
                        classNames.cellEditorPopover,
                    )}
                    classes={{
                        paper: classnames(
                            'data-grid-popover-paper',
                            'data-grid-cell-editor-popover-paper',
                            classNames.cellEditorPopoverPaper,
                        ),
                    }}
                    anchorEl={popover}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: popoverHorizontalPosition,
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: popoverHorizontalPosition,
                    }}>
                    <DataGridCellEditor
                        initialValue={cell?.content?.editableValue}
                        ContentComponent={cell?.content?.EditableContentComponent}
                        colName={column?.name}
                        rowKey={rowKey}
                        close={closePopover}
                    />
                </Popover>
            }
        </>
    )
}

export default DataGridBodyCell;
